import { useEffect, useState } from 'react';
import isImageAttachment from '../../../helpers/isImageAttachment';
import isVideoAttachment from '../../../helpers/isVideoAttachment';
import { LIGHTBOX_BODY_PADDING, LIGHTBOX_HEADER_PADDING } from './lightboxSafeArea';

export interface AttachmentLike {
  name?: string;
  mimetype?: string;
  url?: string;
}

interface SupportAttachmentsMobileProps {
  attachments: AttachmentLike[];
  /** Resolves the display name for an attachment (callers may have richer logic). */
  resolveName: (attachment: AttachmentLike, index: number) => string;
}

// Compact, Trello-like list layout for small screens (thumbnail on the left, info on the right).
const THUMB_SIZE = 56;
const LINE = '#e9ecef';
const INK = '#2b3142';
const INK_MUTE = '#8b94a3';

const FILE_TYPES: Record<string, { color: string; icon: string }> = {
  pdf: { color: '#e85d75', icon: 'fa-file-pdf' },
  doc: { color: '#3b7ddd', icon: 'fa-file-word' },
  docx: { color: '#3b7ddd', icon: 'fa-file-word' },
  xls: { color: '#22a06b', icon: 'fa-file-excel' },
  xlsx: { color: '#22a06b', icon: 'fa-file-excel' },
  csv: { color: '#22a06b', icon: 'fa-file-csv' },
  zip: { color: '#f0a02a', icon: 'fa-file-archive' },
  rar: { color: '#f0a02a', icon: 'fa-file-archive' },
  txt: { color: '#6c757d', icon: 'fa-file-lines' },
  mp4: { color: '#7b5cd6', icon: 'fa-file-video' },
  mov: { color: '#7b5cd6', icon: 'fa-file-video' },
  webm: { color: '#7b5cd6', icon: 'fa-file-video' },
  m4v: { color: '#7b5cd6', icon: 'fa-file-video' },
};

const playBadgeStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(15,20,32,.28)',
  color: '#fff',
  fontSize: 18,
  pointerEvents: 'none',
};

const extensionOf = (value?: string): string => {
  if (!value) return '';
  const base = value.split('#')[0].split('?')[0].split('/').filter(Boolean).pop() || '';
  const dot = base.lastIndexOf('.');
  return dot < 0 ? '' : base.slice(dot + 1).toLowerCase();
};

const fileKindOf = (attachment: AttachmentLike, displayName: string) => {
  const ext = extensionOf(displayName) || extensionOf(attachment.name) || extensionOf(attachment.url);
  const type = FILE_TYPES[ext];
  if (type) return { ext, ...type };
  return { ext, color: '#6c757d', icon: 'fa-file' };
};

const middleTruncate = (name: string, max = 30): string => {
  if (name.length <= max) return name;
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 ? name.slice(dot) : '';
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const room = max - ext.length - 1;
  if (room < 4) return `…${name.slice(-(max - 1))}`;
  const head = Math.ceil(room * 0.6);
  const tail = room - head;
  return `${stem.slice(0, head)}…${stem.slice(stem.length - tail)}${ext}`;
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  padding: '8px 10px',
  border: `1px solid ${LINE}`,
  borderRadius: 8,
  background: '#fff',
  textDecoration: 'none',
  cursor: 'pointer',
  textAlign: 'left',
};

const thumbBoxStyle: React.CSSProperties = {
  width: THUMB_SIZE,
  height: THUMB_SIZE,
  flexShrink: 0,
  borderRadius: 6,
  overflow: 'hidden',
  background: '#f4f6f9',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const SupportAttachmentsMobile = ({ attachments, resolveName }: SupportAttachmentsMobileProps): JSX.Element | null => {
  const [lightbox, setLightbox] = useState<{ url: string; name: string; isVideo: boolean } | null>(null);

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lightbox]);

  if (!attachments || attachments.length === 0) return null;

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {attachments.map((attachment, index) => {
          const name = resolveName(attachment, index);
          const key = `${attachment.url || attachment.name || 'att'}-${index}`;
          const truncated = middleTruncate(name);
          const isImage = isImageAttachment(attachment);
          const isVideo = !isImage && isVideoAttachment(attachment);
          const kind = fileKindOf(attachment, name);

          const info = (
            <div style={{ minWidth: 0, flex: '1 1 auto' }}>
              <div
                title={name}
                style={{
                  color: INK,
                  fontWeight: 700,
                  fontSize: 13,
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {truncated}
              </div>
              <div
                style={{ color: INK_MUTE, fontWeight: 600, fontSize: 10.5, marginTop: 2, textTransform: 'uppercase' }}
              >
                {isImage ? 'image' : kind.ext || (isVideo ? 'video' : 'file')}
              </div>
            </div>
          );

          if (isImage || isVideo) {
            return (
              <button
                key={key}
                type="button"
                aria-label={`Open attachment ${name}`}
                onClick={() => setLightbox({ url: attachment.url || '', name, isVideo })}
                style={{ ...rowStyle, font: 'inherit' }}
              >
                <div style={{ ...thumbBoxStyle, position: 'relative' }}>
                  {isVideo ? (
                    <>
                      <video
                        src={attachment.url}
                        preload="metadata"
                        muted
                        playsInline
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                      <span style={playBadgeStyle}>
                        <i className="fas fa-play-circle" aria-hidden="true" />
                      </span>
                    </>
                  ) : (
                    <img
                      src={attachment.url}
                      alt={name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  )}
                </div>
                {info}
                <i
                  className="fas fa-chevron-right"
                  style={{ color: INK_MUTE, fontSize: 12, flexShrink: 0 }}
                  aria-hidden="true"
                />
              </button>
            );
          }

          return (
            <a
              key={key}
              href={attachment.url}
              target="_blank"
              rel="noreferrer"
              aria-label={`Open attachment ${name}`}
              style={rowStyle}
            >
              <div style={thumbBoxStyle}>
                <i className={`fas ${kind.icon}`} style={{ fontSize: 24, color: kind.color }} aria-hidden="true" />
              </div>
              {info}
              <i
                className="fas fa-external-link-alt"
                style={{ color: INK_MUTE, fontSize: 12, flexShrink: 0 }}
                aria-hidden="true"
              />
            </a>
          );
        })}
      </div>

      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={lightbox.name}
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1060,
            background: 'rgba(15,20,32,.78)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              padding: LIGHTBOX_HEADER_PADDING,
              color: '#fff',
            }}
          >
            <span
              title={lightbox.name}
              style={{
                fontWeight: 700,
                fontSize: 14,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {lightbox.name}
            </span>
            <span style={{ display: 'inline-flex', gap: 8, flexShrink: 0 }}>
              <a
                href={lightbox.url}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="btn btn-sm btn-light"
                title="Open in a new tab"
              >
                <i className="fas fa-external-link-alt" />
              </a>
              <a
                href={lightbox.url}
                download={lightbox.name}
                onClick={(e) => e.stopPropagation()}
                className="btn btn-sm btn-light"
                title="Download"
              >
                <i className="fas fa-download" />
              </a>
              <button
                type="button"
                onClick={() => setLightbox(null)}
                className="btn btn-sm btn-light"
                title="Close (Esc)"
              >
                <i className="fas fa-times" />
              </button>
            </span>
          </div>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: LIGHTBOX_BODY_PADDING,
              minHeight: 0,
            }}
          >
            {lightbox.isVideo ? (
              <video
                src={lightbox.url}
                controls
                autoPlay
                playsInline
                style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 6 }}
              />
            ) : (
              <img
                src={lightbox.url}
                alt={lightbox.name}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 6 }}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SupportAttachmentsMobile;
